/**
 * tableRowFilter.ts — drop table rows that fail a predicate, before segmentation.
 *
 * The review screens hide whole top-level blocks that fail a filter, and a table
 * is one block: as soon as one row passes, the ENTIRE table used to render.
 * Users want just the relevant rows. This transform removes the failing <tr>s
 * from every table, so the renderer simply receives a table with fewer rows.
 *
 * Rules:
 *  - Header rows (a <thead> row, or any row containing a <th>) are always kept,
 *    so the surviving rows stay readable in context.
 *  - Rows tied together by a `rowspan` form one group, kept or dropped as a
 *    unit — dropping the row that starts a spanned cell (or a row it reaches
 *    into) would shift every cell below it into the wrong column.
 *  - With `keepTableIfHeaderMatches`, a table where no body row passes but a
 *    header row does is returned whole (searching for a column name should show
 *    the column, not an empty table).
 *
 * Top-level node identity is preserved (tables stay tables, in place), so the
 * caller's parallel `elementIds` array stays aligned. A table left with only
 * header rows has no passing content and is hidden downstream by the normal
 * block-level filter — no special-casing needed here.
 */

import { HastNode, ElementNode } from "./hast";
import { computeTableLayout } from "./tableLayout";

function isElement(n: HastNode): n is ElementNode {
    return n.type === "element";
}

const ROW_CONTAINERS = new Set(["thead", "tbody", "tfoot"]);

/** A header row is one that holds (or is) a <th>. */
function containsTh(node: HastNode): boolean {
    if (!isElement(node)) return false;
    if (node.tagName === "th") return true;
    return node.children.some(containsTh);
}

interface Row {
    tr: ElementNode;
    header: boolean;
}

/** The table's rows in source order, whether direct children or in a row group. */
function tableRows(table: ElementNode): Row[] {
    const rows: Row[] = [];
    for (const c of table.children) {
        if (!isElement(c)) continue;
        if (c.tagName === "tr") {
            rows.push({ tr: c, header: containsTh(c) });
        } else if (ROW_CONTAINERS.has(c.tagName)) {
            for (const r of c.children) {
                if (isElement(r) && r.tagName === "tr") {
                    rows.push({ tr: r, header: c.tagName === "thead" || containsTh(r) });
                }
            }
        }
    }
    return rows;
}

/**
 * Partition row indices into rowspan groups: consecutive rows where a cell in
 * one row spans down into the next. Rowspans only reach forward, so the groups
 * are contiguous ranges.
 */
function rowspanGroups(rows: Row[]): number[][] {
    const reach = rows.map((_, i) => i);
    for (const p of computeTableLayout(rows.map((r) => r.tr)).placements) {
        const last = Math.min(p.rowIdx + p.rowSpan - 1, rows.length - 1);
        reach[p.rowIdx] = Math.max(reach[p.rowIdx], last);
    }
    const groups: number[][] = [];
    let i = 0;
    while (i < rows.length) {
        const group: number[] = [];
        let end = reach[i];
        for (; i <= end; i++) {
            group.push(i);
            end = Math.max(end, reach[i]);
        }
        groups.push(group);
    }
    return groups;
}

export interface TableRowFilterOptions {
    /** Return the table whole when no body row passes but a header row does. */
    keepTableIfHeaderMatches?: boolean;
}

function filterTable(
    table: ElementNode,
    rowMatches: (tr: ElementNode) => boolean,
    opts: TableRowFilterOptions,
): ElementNode {
    const rows = tableRows(table);
    const kept = new Set<ElementNode>();
    let bodyMatched = false;

    for (const group of rowspanGroups(rows)) {
        const body = group.filter((i) => !rows[i].header);
        const keepBody = body.some((i) => rowMatches(rows[i].tr));
        if (keepBody) bodyMatched = true;
        for (const i of group) {
            if (rows[i].header || keepBody) kept.add(rows[i].tr);
        }
    }

    if (
        !bodyMatched &&
        opts.keepTableIfHeaderMatches &&
        rows.some((r) => r.header && rowMatches(r.tr))
    ) {
        return table;
    }
    if (kept.size === rows.length) return table;

    const filterChildren = (children: HastNode[]): HastNode[] => {
        const out: HastNode[] = [];
        for (const c of children) {
            if (isElement(c) && c.tagName === "tr") {
                if (kept.has(c)) out.push(c);
            } else if (isElement(c) && ROW_CONTAINERS.has(c.tagName)) {
                out.push({ ...c, children: filterChildren(c.children) });
            } else {
                out.push(c); // caption / colgroup / whitespace text — leave as-is
            }
        }
        return out;
    };
    return { ...table, children: filterChildren(table.children) };
}

/** Remove table rows failing `rowMatches` (header rows and rowspan groups kept intact). */
export function filterTableRows(
    nodes: HastNode[],
    rowMatches: (tr: ElementNode) => boolean,
    opts: TableRowFilterOptions = {},
): HastNode[] {
    const transform = (node: HastNode): HastNode => {
        if (!isElement(node)) return node;
        if (node.tagName === "table") return filterTable(node, rowMatches, opts);
        return { ...node, children: node.children.map(transform) };
    };
    return nodes.map(transform);
}

/** Every `data-block-key` in the subtree (node inclusive). */
function blockKeysWithin(node: HastNode, out: string[] = []): string[] {
    if (!isElement(node)) return out;
    const key = node.properties["data-block-key"];
    if (key) out.push(key);
    for (const c of node.children) blockKeysWithin(c, out);
    return out;
}

/** Remove table rows none of whose block keys pass `keyPasses` (header rows and rowspan groups kept). */
export function filterTableRowsByKey(nodes: HastNode[], keyPasses: (key: string) => boolean): HastNode[] {
    return filterTableRows(nodes, (tr) => blockKeysWithin(tr).some(keyPasses));
}
